import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Loader2, ListTodo } from "lucide-react";
import api from "../lib/api";

const PRIORITY_STYLES = {
  high: "border-[#7a4a30]/60 text-[#E5B493] bg-[#7a4a30]/15",
  medium: "border-champagne/40 text-champagne bg-champagne/10",
  low: "border-seam text-textmuted bg-elevated",
};

const STATUS_OPTIONS = [
  { value: "todo", label: "To do" },
  { value: "doing", label: "In progress" },
  { value: "done", label: "Done" },
];

const TaskList = ({ appId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("medium");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/apps/${appId}/tasks`);
      setTasks(data);
    } catch (e) {
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/apps/${appId}/tasks`, {
        title: newTitle.trim(),
        priority: newPriority,
      });
      setTasks((t) => [data, ...t]);
      setNewTitle("");
      setNewPriority("medium");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create task");
    } finally {
      setAdding(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/apps/${appId}/tasks/generate`);
      setTasks((t) => [...data, ...t]);
      toast.success(`The concierge proposed ${data.length} tasks.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const update = async (taskId, patch) => {
    try {
      const { data } = await api.patch(`/apps/${appId}/tasks/${taskId}`, patch);
      setTasks((t) => t.map((x) => (x.id === taskId ? data : x)));
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const remove = async (taskId) => {
    const prev = tasks;
    setTasks((t) => t.filter((x) => x.id !== taskId));
    try {
      await api.delete(`/apps/${appId}/tasks/${taskId}`);
    } catch (err) {
      setTasks(prev);
      toast.error("Delete failed");
    }
  };

  return (
    <div className="regal-card overflow-hidden" data-testid="task-list">
      <div className="px-6 py-4 border-b border-seam flex items-center justify-between bg-elevated/40">
        <div className="flex items-center gap-3">
          <ListTodo className="w-4 h-4 text-champagne" strokeWidth={1.5} />
          <div>
            <p className="label-eyebrow">Roadmap</p>
            <h3 className="font-heading text-2xl mt-1">Tasks</h3>
          </div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          data-testid="generate-tasks-btn"
          className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-champagne/40 text-champagne hover:bg-champagne/10 transition-colors disabled:opacity-60"
        >
          {generating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          AI suggest
        </button>
      </div>

      <form onSubmit={add} className="px-6 py-4 border-b border-seam flex gap-2">
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Add a task…"
          data-testid="task-input"
          className="flex-1 bg-obsidian border border-seam rounded-md px-3 py-2 text-sm text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
        />
        <select
          value={newPriority}
          onChange={(e) => setNewPriority(e.target.value)}
          data-testid="task-priority-select"
          className="bg-obsidian border border-seam rounded-md px-3 py-2 text-sm text-textmain focus:outline-none focus:border-champagne"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <button
          type="submit"
          disabled={adding || !newTitle.trim()}
          data-testid="task-add-btn"
          className="px-3 py-2 rounded-md bg-champagne text-obsidian hover:bg-champagne-soft transition-all disabled:opacity-50"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </form>

      <div className="divide-y divide-seam max-h-[420px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 text-champagne animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="px-6 py-10 text-center text-textmuted text-sm">
            No tasks yet. Add one or ask the concierge to propose a roadmap.
          </div>
        ) : (
          tasks.map((t) => (
            <div
              key={t.id}
              data-testid={`task-row-${t.id}`}
              className="px-6 py-4 flex items-start gap-3 hover:bg-elevated/40 transition-colors"
            >
              <input
                type="checkbox"
                checked={t.status === "done"}
                onChange={(e) =>
                  update(t.id, { status: e.target.checked ? "done" : "todo" })
                }
                className="mt-1.5 w-4 h-4 accent-champagne cursor-pointer"
                data-testid={`task-checkbox-${t.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p
                    className={`text-sm font-medium ${
                      t.status === "done" ? "line-through text-textmuted" : "text-textmain"
                    }`}
                  >
                    {t.title}
                  </p>
                  <span
                    className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border ${
                      PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.medium
                    }`}
                  >
                    {t.priority}
                  </span>
                  {t.source === "ai" && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full border border-champagne/30 text-champagne bg-champagne/10">
                      <Sparkles className="w-3 h-3" strokeWidth={1.6} />
                      AI
                    </span>
                  )}
                </div>
                {t.description && (
                  <p className="text-xs text-textmuted mt-1 leading-relaxed">
                    {t.description}
                  </p>
                )}
              </div>
              <select
                value={t.status}
                onChange={(e) => update(t.id, { status: e.target.value })}
                className="bg-obsidian border border-seam rounded-md px-2 py-1 text-xs text-textmuted focus:outline-none focus:border-champagne"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(t.id)}
                data-testid={`task-delete-${t.id}`}
                className="p-1.5 rounded-md text-textmuted hover:text-[#E5A09E] hover:bg-elevated transition-colors"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TaskList;
